import { EditNoteOutlined, SpatialAudioOffSharp } from "@mui/icons-material";
import { Badge, Box, Button, Card, CircularProgress, Divider, Typography } from "@mui/material";
import { UUID } from "crypto";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import emitSnackbar from "../../../../emitSnackbar";
import { TOP_BAR_HEIGHT } from "../../../../pages/AppLayout";
import useStudyMetadataStore from "../../useStudyMetadataStore";
import { useEditStudyContext } from "../useEditStudyContext";
import { useManageStudiesStore } from "../useManageStudiesStore";
import EditStudyAudioFiles from "./EditStudyAudioFiles";
import EditStudyOverview from "./EditStudyOverview";
import { FileType } from "../manageStudyModels";

export default function EditStudy() {
  const { studyId } = useParams<{ studyId: UUID }>();

  const navigate = useNavigate();
  const { study: study_metadata, isLoading: isStudyMetaDataLoading, MetadataQuery } = useStudyMetadataStore(studyId);

  const { completeStudy } = useManageStudiesStore();

  const [currentIndex, setCurrentIndex] = useState(0);

  const EditStudyContext = useEditStudyContext(studyId!);
  const { study, isPowerPoint, canPublish, canSave } = EditStudyContext;
  const has_screener_summary = !!EditStudyContext.getGuideFile(FileType.SCREENER_SUMMARY)?.uploaded_file_guid;

  useEffect(() => console.debug("edit_study", study), [study]);

  useEffect(() => {
    // Force a refresh of the study, and perform a one time sync with the internal edit model
    MetadataQuery.refetch().then((response) => {
      const _study_metadata = response.data;
      if (_study_metadata) {
        EditStudyContext.syncStudyModel(_study_metadata);
      }
    });
  }, []);

  const handleSave = async () => {
    try {
      const saved_study_metadata = await EditStudyContext.save();
      console.log("save:study_metadata", saved_study_metadata);
      emitSnackbar("Study saved", "success", { autoHideDuration: 3000 });
    } catch (error) {
      emitSnackbar("Study save failed", "error", { autoHideDuration: 3000 });
      throw error;
    }

    // Refresh the local state since its more work to push the update from study.save() at this time
    MetadataQuery.refetch();
  };

  if (isStudyMetaDataLoading || !study_metadata || !studyId || !study) {
    return (
      <Box
        sx={{
          marginTop: `${TOP_BAR_HEIGHT}px`,
          paddingX: "6rem",
          paddingY: "2rem",
          width: "100%",
          display: "flex",
          height: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        marginTop: `${TOP_BAR_HEIGHT}px`,
        paddingX: "6rem",
        paddingY: "2rem",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${TOP_BAR_HEIGHT}px)`,
      }}
    >
      <Box
        sx={{
          flex: "0 0 auto",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "2rem",
          display: "flex",
        }}
      >
        <Typography sx={{ fontSize: "24px", color: "#004677", fontWeight: "400" }}>Edit Study</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            sx={{ color: "#003087" }}
            onClick={() => navigate(-1)}
          >
            CANCEL
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            sx={{ color: "white", backgroundColor: "#003087" }}
            disabled={!canSave}
            onClick={handleSave}
          >
            SAVE
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            sx={{ color: "white", backgroundColor: "#003087", minWidth: "150px" }}
            disabled={!canPublish}
            onClick={() => completeStudy(studyId!)}
          >
            {EditStudyContext.isLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                <CircularProgress size={24} sx={{ color: "white" }} />
              </Box>
            ) : (
              "COMPLETE STUDY"
            )}
          </Button>
        </Box>
      </Box>

      <Card
        sx={{
          width: "100%",
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
          flex: "1",
          borderRadius: "10px",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: "700", color: "#13294B" }}>
              {study.vendor} | {study.study_name}
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "12px", fontWeight: "400", color: "#13294B" }}>
              Created {dayjs(study_metadata.created_ts).format("MM/DD/YYYY")} by{" "}
              {study_metadata?.owner?.auth_user_email}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <Box
              onClick={() => {
                setCurrentIndex(0);
              }}
              sx={{
                display: "flex",
                backgroundColor: currentIndex === 0 ? "rgba(0, 48, 135, 0.08)" : "transparent",
                borderRadius: "20%",
                justifyContent: "center",
                alignItems: "center",
                padding: "0.3rem",
                cursor: "pointer",
              }}
            >
              <EditNoteOutlined />
            </Box>
            <Box
              sx={{
                display: "flex",
                backgroundColor: currentIndex === 1 ? "rgba(0, 48, 135, 0.08)" : "transparent",
                borderRadius: "20%",
                justifyContent: "center",
                alignItems: "center",
                padding: "0.3rem",
                ...(has_screener_summary
                  ? {
                      cursor: "pointer",
                    }
                  : {
                      "& svg *": { opacity: 0.5 },
                    }),
              }}
              title={
                has_screener_summary ? undefined : "Please select a Screener Summary to enable editing Audio Files"
              }
              onClick={() => {
                // Only allow transitioning to the Audio file page if a screener summary is present
                if (has_screener_summary) {
                  setCurrentIndex(1);
                }
              }}
            >
              <Badge
                overlap="circular"
                badgeContent=""
                variant="dot"
                color="default"
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  backgroundColor: " rgba(0, 48, 135, 0.08))",
                  display: isPowerPoint ? "none" : "flex",
                  "& .MuiBadge-dot": { minWidth: "8px", height: "8px", right: "10px", top: "4px" },
                }}
              >
                <SpatialAudioOffSharp />
              </Badge>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ my: "1rem" }}></Divider>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
            padding: "0.5rem",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {currentIndex === 0 && <EditStudyOverview study_metadata={study_metadata} edit_context={EditStudyContext} />}
          {currentIndex === 1 && <EditStudyAudioFiles study={study_metadata} edit_context={EditStudyContext} />}
        </Box>
      </Card>
    </Box>
  );
}
